import ArrowLeft from "@/assets/icons/ArrowLeft";
import AppButton from "@/components/shared/AppButton";
import { ButtonGroup } from "@/components/ui/button";
import { FormControl, FormControlError, FormControlErrorText, FormControlLabel, FormControlLabelText } from "@/components/ui/form-control";
import { Input, InputField } from "@/components/ui/input";
import { VStack } from "@/components/ui/vstack";
import { zodResolver } from "@hookform/resolvers/zod";
import { useT } from "@transifex/react";
import { useRouter } from "expo-router";
import { useEffect } from "react";
import { type Control, Controller, useForm } from "react-hook-form";
import * as z from "zod";

const formPlateSchema = z.object({
  plate: z
    .string()
    .min(2, "License plate must be at least 2 characters long")
    .max(12, "License plate must be no longer than 12 characters")
    .regex(/^[A-Z0-9]+$/, "License plate must contain only uppercase letters and numbers"),
  nickname: z.string().min(1, "Insert a nickname for your car"),
});

type FormValues = z.infer<typeof formPlateSchema>;

interface FormInputProps {
  name: keyof FormValues;
  label: string;
  placeholder: string;
  control: Control<FormValues>;
  error?: string;
}

const FormInput = ({ name, label, placeholder, control, error }: FormInputProps) => {
  const t = useT();
  return (
    <FormControl isInvalid={!!error} key="form-input">
      <FormControlLabel className="flex-col items-start w-full gap-[10px]">
        <FormControlLabelText className="font-[600] leading-[20px] text-[14px] text-typography-900">{label}</FormControlLabelText>

        <Controller
          name={name}
          control={control}
          render={({ field: { onChange, onBlur, value } }) => (
            <Input className="bg-background-100 h-[52px] px-[16px] py-[8px] rounded-[6px] w-full">
              <InputField
                type="text"
                className="font-[400] leading-[22px] text-[16px] p-0 text-typography-900"
                placeholder={placeholder}
                onBlur={onBlur}
                onChangeText={(text: string) => {
                  if (name === "plate") {
                    onChange(text.toUpperCase());
                  } else {
                    onChange(text);
                  }
                }}
                value={value}
                maxLength={12}
              />
            </Input>
          )}
        />
      </FormControlLabel>

      {error && (
        <FormControlError>
          <FormControlErrorText className="leading-[22px] text-[16px] text-[400] text-error-500">{t(error)}</FormControlErrorText>
        </FormControlError>
      )}
    </FormControl>
  );
};

interface PlateFormProps {
  defaultValues: FormValues;
  onSubmit: (data: FormValues) => void;
  loading?: boolean;
}

const PlateForm = ({ defaultValues, onSubmit, loading }: PlateFormProps) => {
  const t = useT();
  const router = useRouter();

  const {
    control,
    handleSubmit,
    formState: { errors },
    reset,
  } = useForm<FormValues>({
    resolver: zodResolver(formPlateSchema),
    defaultValues,
  });

  useEffect(() => {
    reset(defaultValues);
  }, [defaultValues, reset]);

  return (
    <VStack className="gap-[20px]">
      <VStack className="gap-[10px]">
        <FormInput control={control} error={errors.plate?.message && t(errors.plate?.message)} name="plate" label={t("License plate")} placeholder="ABC123" />
        <FormInput
          control={control}
          error={errors.nickname?.message && t(errors.nickname?.message)}
          name="nickname"
          label={t("Vehicle nickname")}
          placeholder="Rust Bucket"
        />
      </VStack>

      <ButtonGroup className="flex-row gap-[12px]">
        <AppButton
          className="flex-1"
          icon={ArrowLeft}
          onPress={() => (router.canGoBack() ? router.back() : router.replace("/my-vehicles"))}
          title={t("Back")}
          variant="outline"
        />
        <AppButton className="flex-1" loading={loading} onPress={handleSubmit(onSubmit)} title={t("Save vehicle")} />
      </ButtonGroup>
    </VStack>
  );
};

export default PlateForm;
